import { fetchOAuthToken } from './oauthService'

const BASE_URL = import.meta.env.VITE_API_BASE_URL

export const createUser = async (user: {
  first_name: string
  last_name: string
  email: string
  phone_number: string
}): Promise<any> => {
  const token = await fetchOAuthToken()
  const url = `${BASE_URL}/api/v2/user/crm`

  const data = {
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    phone_numbers: [
      {
        value: user.phone_number,
        preferred: true,
        type: 'privateMobile',
      },
    ],
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    })

    const responseData = await response.json()

    if (!response.ok) {
      if (response.status === 400 && responseData.error) {
        throw new Error(responseData.error.message)
      }
      throw new Error('Failed to create user, try log in instead')
    }

    return responseData
  } catch (error) {
    console.error('Error creating user:', error)
    throw error
  }
}

interface CrmTokenResponse {
  access_token: string
  refresh_token: string
  expires_in: number
  token_type: string
}

export const fetchCrmToken = async (
  email: string,
  password: string,
  upgradeToken?: string,
): Promise<CrmTokenResponse> => {
  const VITE_API_KEY = import.meta.env.VITE_API_KEY
  const url = `${BASE_URL}/oauth/v2/token`

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${VITE_API_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        grant_type: 'https://com.sqills.s3.oauth.crm',
        email,
        password,
        ...(upgradeToken && { upgrade_token: upgradeToken }),
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error('Failed to fetch CRM token')
    }

    return data
  } catch (error) {
    console.error('Error fetching CRM token:', error)
    throw error
  }
}

export const getUserDetails = async (token: string): Promise<any> => {
  const url = `${BASE_URL}/api/v2/user/crm`

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error('Failed to get user details')
    }

    return data
  } catch (error) {
    console.error('Error getting user details:', error)
    throw error
  }
}

export const requestPasswordReset = async (email: string): Promise<void> => {
  const token = await fetchOAuthToken()
  const url = `${BASE_URL}/api/v2/user/crm/create-new-password`

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: Object.assign({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      }),
      body: JSON.stringify({ email }),
    })

    if (response.status === 204) {
      return
    }

    if (!response.ok) {
      const responseData = await response.json()
      throw new Error(responseData.error?.message || 'Failed to request password reset')
    }
  } catch (error) {
    console.error('Error requesting password reset:', error)
    throw error
  }
}

export const updateUserDetails = async (user: any, token: string): Promise<any> => {
  if (!token) {
    throw new Error('No CRM token available')
  }

  const url = `${BASE_URL}/api/v2/user/crm`

  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(user),
    })

    if (!response.ok) {
      const responseData = await response.json()
      throw new Error(responseData.error.message || 'Failed to update user details')
    }

    return await response.json()
  } catch (error) {
    console.error('Error updating user details:', error)
    throw error
  }
}

export const getBookings = async (params: {
  token: string
  userId?: string
  userEmail?: string
  url?: string
}): Promise<any> => {
  const { token, userId, userEmail, url: customUrl } = params

  if (!token) {
    throw new Error('No CRM token available')
  }

  let url = customUrl
  if (!url) {
    if (!userId || !userEmail) {
      throw new Error('User ID and email are required')
    }
    url = `${BASE_URL}/api/v3/bookings?crm_reference=${userId}&customer_email=${userEmail}&include_cancelled_bookings=true&include_cancelled_segments=true&sort=-create_datetime`
  }

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    })

    const contentType = response.headers.get('content-type')
    if (!response.ok) {
      if (contentType?.includes('application/json')) {
        const responseData = await response.json()
        console.error('API error response:', responseData)
        throw new Error(responseData.error?.message || 'Failed to fetch bookings')
      } else {
        const text = await response.text()
        console.error('API error response (non-JSON):', text)
        throw new Error('Failed to fetch bookings')
      }
    }

    if (!contentType?.includes('application/json')) {
      const text = await response.text()
      console.error('Unexpected response type:', contentType, 'Response:', text)
      throw new Error('Unexpected response type from server')
    }

    const data = await response.json()
    if (!data || !data.bookings) {
      console.error('Invalid response format:', data)
      throw new Error('Invalid response format from server')
    }
    return data
  } catch (error) {
    console.error('Error fetching bookings:', error)
    throw error
  }
}

export const deleteAccount = async (token: string): Promise<void> => {
  if (!token) {
    throw new Error('No CRM token available. Please log in again.')
  }
  const url = `${BASE_URL}/api/v2/user/crm`

  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    })

    if (!response.ok) {
      const responseData = await response.json()
      if (responseData.error) {
        throw new Error(responseData.error.message)
      }
      throw new Error('Failed to delete account')
    }
  } catch (error) {
    console.error('Error deleting account:', error)
    throw error
  }
}

export const confirmNewPassword = async (passwordToken: string, password: string): Promise<any> => {
  const token = await fetchOAuthToken()
  const url = `${BASE_URL}/api/v3/crm/customers/${passwordToken}/password`

  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ password }),
    })

    if (!response.ok) {
      const responseData = await response.json()
      console.error('Full API error response:', JSON.stringify(responseData, null, 2))

      if (responseData.errors?.[0]?.code === 'customer_password_weak') {
        throw new Error(
          'Password must be at least 8 characters and contain at least one uppercase letter, one digit, and one symbol',
        )
      }

      if (responseData.error?.message) {
        throw new Error(responseData.error.message)
      }

      if (responseData.errors?.[0]?.context?.expected) {
        throw new Error(responseData.errors[0].context.expected)
      }

      throw new Error('Failed to confirm new password')
    }

    const data = await response.json()
    console.log('Password confirmation successful:', data)
    return data
  } catch (error) {
    console.error('Error confirming new password:', error)
    throw error
  }
}
